import { useState } from "react";
import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";
import { useQueryClient } from "@tanstack/react-query";
import { XIcon } from "@phosphor-icons/react/dist/icons/X";
import { ArrowLeftIcon } from "@phosphor-icons/react/dist/icons/ArrowLeft";
import { CheckCircleIcon } from "@phosphor-icons/react/dist/icons/CheckCircle";
import { LightningIcon } from "@phosphor-icons/react/dist/icons/Lightning";
import { LockSimpleIcon } from "@phosphor-icons/react/dist/icons/LockSimple";
import {
  createSourcingSubscription,
  confirmSourcingSubscription,
  buySourcingCredits,
  confirmBuyCredits,
} from "../Wallet/api/postSourcingSubscription.ts";
import { handleBookCall } from "../../../api/ApiHelpers.ts";
import { useAppSelector } from "../../../redux/store.ts";

declare global {
  interface Window {
    Razorpay: any;
  }
}

const GST_RATE = 0.18;
const addGst = (base: number) => Math.round(base * (1 + GST_RATE));
const gstAmount = (base: number) => Math.round(base * GST_RATE);
const formatInr = (amount: number) => `₹${amount.toLocaleString("en-IN")}`;

const PLANS = [
  {
    key: "starter" as const,
    label: "Starter",
    basePrice: 7000,
    price: "₹7,000",
    totalPrice: formatInr(addGst(7000)),
    gst: formatInr(gstAmount(7000)),
    period: "/ month",
    badge: null,
    contactSales: false,
    features: [
      "Unlimited AI Search Across 90M+ Profiles",
      "Unlimited Roles and JDs",
      "1 user email",
    ],
    credits: {
      total: 100,
      rates: [
        { label: "Email", value: "1 Credit" },
        { label: "Phone", value: "2 Credits" },
      ],
    },
  },
  {
    key: "pro" as const,
    label: "Pro",
    basePrice: 25000,
    price: "₹25,000",
    totalPrice: formatInr(addGst(25000)),
    gst: formatInr(gstAmount(25000)),
    period: "/ month",
    badge: "Recommended",
    contactSales: false,
    features: [
      "Unlimited AI Search Across 90M+ Profiles",
      "Unlimited Roles and JDs",
      "Upto 5 user emails",
    ],
    credits: {
      total: 500,
      rates: [
        { label: "Email", value: "1 Credit" },
        { label: "Phone", value: "2 Credits" },
      ],
    },
  },
  {
    key: "enterprise" as const,
    label: "Enterprise",
    price: "Custom",
    period: "",
    badge: null,
    contactSales: true,
    description: "For larger teams, custom integration requirements and companies filling 100+ roles per year",
    features: [],
    credits: null,
  },
];

// Seat-based (billing_version 2) companies see the same cards with the
// company-level Starter/Team plans. Team is priced per user via Razorpay
// quantity, 2-5 seats, 750 credits per user per cycle.
const TEAM_MIN_SEATS = 2;
const TEAM_MAX_SEATS = 5;
const TEAM_CREDITS_PER_SEAT = 750;
// Shared by the Starter and Team cards — only the user count differs.
const V2_CORE_FEATURES = [
  "Unlimited Searches across 90M+ Profiles",
  "Unlimited Profile Downloads",
  "Unlimited Roles",
  "AI-Video Interviews",
  "AI-Phone Screening",
  "Coding Tests",
  "Aptitude Tests",
  "Psychometric Tests",
];
const V2_PLANS = [
  {
    key: "starter" as const,
    label: "Starter",
    basePrice: 7500,
    price: "₹7,500",
    totalPrice: formatInr(addGst(7500)),
    gst: formatInr(gstAmount(7500)),
    period: "/ month",
    badge: null,
    contactSales: false,
    features: [...V2_CORE_FEATURES, "1 User"],
    credits: {
      total: 500,
      rates: [],
    },
  },
  {
    key: "team" as const,
    label: "Team",
    basePrice: 7500,
    price: "₹7,500",
    totalPrice: formatInr(addGst(7500)),
    gst: formatInr(gstAmount(7500)),
    period: "/ user / month",
    badge: "Recommended",
    savings: "Save 33%",
    contactSales: false,
    features: [...V2_CORE_FEATURES, "2–5 Users"],
    credits: {
      total: TEAM_CREDITS_PER_SEAT,
      rates: [],
    },
  },
  {
    key: "enterprise" as const,
    label: "Enterprise",
    price: "Custom",
    period: "",
    badge: null,
    contactSales: true,
    description: "Bespoke solution that includes everything in Team plans, plus:",
    features: [
      "Team management features",
      "Account management",
      "Compliance readiness",
      "Integration with ATS / HRMS",
    ],
    credits: null,
  },
];

function loadRazorpayScript(): Promise<boolean> {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

interface Props {
  slug: string;
  mode?: "subscribe" | "buy-credits";
  onClose: () => void;
  onSuccess: () => void;
  hideClose?: boolean;
  onBack?: () => void;
}

const SourcingPlanModal = ({ slug, mode = "subscribe", onClose, onSuccess, hideClose = false, onBack }: Props) => {
  const queryClient = useQueryClient();
  const { user } = useAppSelector((state) => state.user);
  const isV2 = user?.billing_version === 2;
  const plans = isV2 ? V2_PLANS : PLANS;
  const [selectedPlan, setSelectedPlan] = useState<"starter" | "pro" | "team" | "enterprise">("starter");
  const [seats, setSeats] = useState(TEAM_MIN_SEATS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const openRazorpaySubscription = async (subscriptionId: string, razorpayKey: string) => {
    const loaded = await loadRazorpayScript();
    if (!loaded) { setError("Failed to load Razorpay. Please try again."); setLoading(false); return; }
    const rzp = new window.Razorpay({
      key: razorpayKey,
      subscription_id: subscriptionId,
      name: "Babblebots AI Talent Discovery",
      description: `${plans.find(p => p.key === selectedPlan)?.label} Plan`,
      handler: async (response: any) => {
        try {
          await confirmSourcingSubscription(slug, {
            plan: selectedPlan,
            ...(isV2 ? { seats: selectedPlan === "team" ? seats : 1 } : {}),
            razorpay_order_id: response.razorpay_order_id ?? "",
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_subscription_id: response.razorpay_subscription_id,
            razorpay_signature: response.razorpay_signature,
          });
          queryClient.invalidateQueries({ queryKey: ["sourcing-access", slug] });
          queryClient.invalidateQueries({ queryKey: ["sourcing-payment-history", slug] });
          onSuccess();
        } catch (e: any) {
          if (e.message === "payment_pending") {
            setError("Your payment is being processed. Access will be activated shortly — please refresh in a few minutes.");
          } else {
            setError("Payment received but activation failed. Please contact support.");
          }
          setLoading(false);
        }
      },
      modal: { ondismiss: () => setLoading(false) },
    });
    rzp.open();
  };

  const openRazorpayOrder = async (orderId: string, razorpayKey: string, amountPaise: number) => {
    const loaded = await loadRazorpayScript();
    if (!loaded) { setError("Failed to load Razorpay. Please try again."); setLoading(false); return; }
    const rzp = new window.Razorpay({
      key: razorpayKey,
      order_id: orderId,
      amount: amountPaise,
      currency: "INR",
      name: "Babblebots Credits",
      description: "Sourcing Credits Top-up",
      handler: async (response: any) => {
        try {
          await confirmBuyCredits(slug, {
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
          });
          queryClient.invalidateQueries({ queryKey: ["sourcing-access", slug] });
          queryClient.invalidateQueries({ queryKey: ["sourcing-payment-history", slug] });
          onSuccess();
        } catch (e: any) {
          if (e.message === "payment_pending") {
            setError("Your payment is being processed. Credits will be added shortly — please refresh in a few minutes.");
          } else {
            setError("Payment received but credits were not added. Please contact support.");
          }
          setLoading(false);
        }
      },
      modal: { ondismiss: () => setLoading(false) },
    });
    rzp.open();
  };

  const handleSubscribe = async () => {
    if (selectedPlan === "enterprise") return;
    setLoading(true);
    setError("");
    try {
      const data = await createSourcingSubscription(
        slug, selectedPlan, selectedPlan === "team" ? seats : 1);
      await openRazorpaySubscription(data.subscription_id, data.razorpay_key);
    } catch (e: any) {
      setError(e.message || "Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  const [customAmount, setCustomAmount] = useState("");

  const customCredits = customAmount ? Math.floor(parseInt(customAmount) / 10) : 0;
  const customAmountNum = parseInt(customAmount) || 0;
  const customValid = customAmountNum >= 1000;

  const handleBuyCredits = async () => {
    setError("");
    if (!customValid) {
      setError("Minimum amount is ₹1,000.");
      return;
    }
    setLoading(true);
    try {
      const data = await buySourcingCredits(slug, { amount_rupees: customAmountNum, tokens: customCredits });
      await openRazorpayOrder(data.order_id, data.razorpay_key, data.amount_paise);
    } catch (e: any) {
      setError(e.message || "Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  const selectedPlanData = plans.find(p => p.key === selectedPlan)!;
  // The subscribe button shows the base price with "+ GST" rather than the
  // GST-inclusive total.
  const subscribePrice =
    isV2 && selectedPlan === "team"
      ? formatInr(7500 * seats)
      : selectedPlanData.price;

  if (mode === "buy-credits") {
    return (
      <Dialog open onClose={hideClose ? () => {} : onClose} className="relative z-[999]">
        <DialogBackdrop className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel
            className="w-full max-w-md overflow-hidden rounded-2xl"
            style={{ background: "#1a1f2e", border: "1px solid rgba(255,255,255,0.08)" }}
          >
            {/* Header strip */}
            <div
              className="flex items-center gap-3 px-6 py-5"
              style={{ background: "linear-gradient(135deg, #1e3a5f 0%, #1a2744 100%)", borderBottom: "1px solid rgba(255,255,255,0.06)" }}
            >
              {onBack && (
                <button onClick={onBack} className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-black/40 text-gray-400 transition-colors hover:bg-black/60 hover:text-gray-200">
                  <ArrowLeftIcon className="h-4 w-4" />
                </button>
              )}
              <div className="flex h-9 w-9 items-center justify-center rounded-lg" style={{ background: "rgba(56,189,248,0.15)" }}>
                <LightningIcon className="h-5 w-5 text-sky-400" weight="fill" />
              </div>
              <div>
                <p className="text-base font-semibold uppercase tracking-widest text-white">Top-up AI Credits</p>
              </div>
              {!hideClose && (
                <button onClick={onClose} className="ml-auto text-gray-500 hover:text-gray-300 transition-colors">
                  <XIcon className="h-5 w-5" />
                </button>
              )}
            </div>

            <div className="p-6">
              <p className="mb-4 text-sm text-gray-400">
                Universal AI Credits that can be used across all Babblebots modules - AI sourcing or screening agents
              </p>

              {/* Custom amount input */}
              <div className="mb-5">
                <div
                  className="flex items-center gap-2 rounded-xl px-4 py-3 transition-all"
                  style={{
                    background: customAmount ? "rgba(56,189,248,0.06)" : "rgba(255,255,255,0.03)",
                    border: customAmount ? "1.5px solid rgba(56,189,248,0.5)" : "1.5px solid rgba(255,255,255,0.07)",
                  }}
                >
                  <span className="text-sm text-gray-400">₹</span>
                  <input
                    type="number"
                    min={1000}
                    placeholder="Enter amount (min ₹1,000)"
                    value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value)}
                    className="flex-1 bg-transparent text-sm text-white placeholder-gray-600 outline-none"
                  />
                  {customAmount && (
                    <span className="text-xs whitespace-nowrap" style={{ color: customValid ? "#38bdf8" : "#f87171" }}>
                      {customValid ? `≈ ${customCredits} credits` : "Min ₹1,000"}
                    </span>
                  )}
                </div>
              </div>

              {error && (
                <p className="mb-4 rounded-lg bg-red-950/40 px-4 py-2 text-sm text-red-400 border border-red-900/50">{error}</p>
              )}
              <button
                onClick={handleBuyCredits}
                disabled={loading}
                className="w-full rounded-xl py-3 text-sm font-semibold text-white transition-opacity disabled:opacity-60"
                style={{ background: "linear-gradient(135deg, #0ea5e9 0%, #6366f1 100%)" }}
              >
                {loading ? "Processing…" : customValid ? `Pay ₹${customAmountNum.toLocaleString("en-IN")} + GST` : "Buy Now"}
              </button>
            </div>
          </DialogPanel>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog open onClose={hideClose ? () => {} : onClose} className="relative z-[999]">
      <DialogBackdrop className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel
          className={`w-full overflow-hidden rounded-2xl ${isV2 ? "max-w-[818px]" : "max-w-3xl"}`}
          style={{ background: "#1a1f2e", border: "1px solid rgba(255,255,255,0.08)" }}
        >
          {/* Header */}
          <div
            className="relative overflow-hidden px-6 py-6"
            style={{ background: "linear-gradient(135deg, #0f2744 0%, #1a1f3a 60%, #1a2235 100%)", borderBottom: "1px solid rgba(255,255,255,0.06)" }}
          >
            {/* Decorative glow */}
            <div
              className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full opacity-20"
              style={{ background: "radial-gradient(circle, #38bdf8 0%, transparent 70%)" }}
            />
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                {onBack && (
                  <button onClick={onBack} className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-black/40 text-gray-400 transition-colors hover:bg-black/60 hover:text-gray-200">
                    <ArrowLeftIcon className="h-4 w-4" />
                  </button>
                )}
                <div
                  className="flex h-10 w-10 items-center justify-center rounded-xl"
                  style={{ background: "linear-gradient(135deg, rgba(56,189,248,0.25) 0%, rgba(99,102,241,0.25) 100%)", border: "1px solid rgba(56,189,248,0.3)" }}
                >
                  <LightningIcon className="h-5 w-5 text-sky-400" weight="fill" />
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-widest text-sky-400">
                    {isV2 ? "Babblebots AI" : "AI Talent Discovery"}
                  </p>
                  <p className="text-xl font-semibold text-white">Choose your plan</p>
                </div>
              </div>
              {!hideClose && (
                <button onClick={onClose} className="text-gray-500 hover:text-gray-300 transition-colors mt-1">
                  <XIcon className="h-5 w-5" />
                </button>
              )}
            </div>
            <p className="mt-3 text-sm text-gray-400">
              {isV2
                ? "Hiring Re-imagined with AI — Better. Faster. Trusted by Enterprises."
                : "The easiest way to discover talent. Period."}
            </p>
          </div>

          {/* Plan cards */}
          <div className={`grid grid-cols-3 gap-3 pb-0 ${isV2 ? "px-5 pt-6" : "p-6"}`}>
            {plans.map((plan) => {
              const isSelected = selectedPlan === plan.key;
              const isPro = plan.key === "pro" || plan.key === "team";
              const isEnterprise = plan.key === "enterprise";

              const accentColor = isEnterprise ? "#d4a03c" : isPro ? "#a5b4fc" : "#38bdf8";
              const radioColor  = isEnterprise ? "#d4a03c" : isPro ? "#6366f1" : "#38bdf8";

              const cardBg = isSelected
                ? isEnterprise
                  ? "linear-gradient(135deg, rgba(180,130,60,0.12) 0%, rgba(120,80,20,0.08) 100%)"
                  : isPro
                    ? "linear-gradient(135deg, rgba(99,102,241,0.15) 0%, rgba(56,189,248,0.08) 100%)"
                    : "rgba(56,189,248,0.08)"
                : isEnterprise
                  ? "linear-gradient(135deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.02) 100%)"
                  : "rgba(255,255,255,0.03)";

              const cardBorder = isSelected
                ? isEnterprise ? "1.5px solid rgba(212,160,60,0.65)"
                  : isPro     ? "1.5px solid rgba(99,102,241,0.7)"
                              : "1.5px solid rgba(56,189,248,0.6)"
                : isEnterprise ? "1.5px solid rgba(212,160,60,0.2)"
                              : "1.5px solid rgba(255,255,255,0.07)";

              const cardShadow = isSelected
                ? isEnterprise ? "0 0 24px rgba(180,130,60,0.15)"
                  : isPro     ? "0 0 20px rgba(99,102,241,0.15)"
                              : "0 0 20px rgba(56,189,248,0.1)"
                : "none";

              return (
                <button
                  key={plan.key}
                  onClick={() => setSelectedPlan(plan.key)}
                  className="flex flex-col rounded-xl p-4 text-left transition-all duration-150"
                  style={{ background: cardBg, border: cardBorder, boxShadow: cardShadow }}
                >
                  {/* ── Top row: radio + badge (always same height) ── */}
                  <div className="mb-4 flex h-5 items-center justify-between">
                    <div
                      className="flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border-2 transition-all"
                      style={{
                        borderColor: isSelected ? radioColor : "rgba(255,255,255,0.2)",
                        background:  isSelected ? radioColor : "transparent",
                      }}
                    >
                      {isSelected && <span className="block h-1.5 w-1.5 rounded-full bg-white" />}
                    </div>
                    {plan.badge || ("savings" in plan && plan.savings) ? (
                      <span className="flex items-center gap-1.5">
                        {"savings" in plan && plan.savings && (
                          <span
                            className="rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                            style={{
                              borderColor: "rgba(52,211,153,0.4)",
                              background: "rgba(52,211,153,0.12)",
                              color: "#6ee7b7",
                            }}
                          >
                            {plan.savings}
                          </span>
                        )}
                        {plan.badge && (
                          <span
                            className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white"
                            style={{ background: "linear-gradient(135deg, #f59e0b, #ef4444)" }}
                          >
                            {plan.badge}
                          </span>
                        )}
                      </span>
                    ) : (
                      <span className="h-5" />
                    )}
                  </div>

                  {/* ── Price (Team recalculates with the seat count) ── */}
                  <div className="mb-1">
                    <div className="flex items-baseline gap-1">
                      <span
                        className="text-2xl font-bold leading-none"
                        style={{ color: isSelected ? accentColor : "#e2e8f0" }}
                      >
                        {isV2 && plan.key === "team" ? formatInr(7500 * seats) : plan.price}
                      </span>
                      {plan.period && (
                        <span className="text-xs text-gray-500">
                          {isV2 && plan.key === "team" ? "/ month" : plan.period}
                        </span>
                      )}
                      {"gst" in plan && (
                        <span className="text-xs text-gray-500">+ GST</span>
                      )}
                    </div>
                  </div>

                  {/* ── Label + subtitle ── */}
                  <p className="mb-0.5 text-sm font-semibold text-gray-200">{plan.label}</p>
                  <p className="text-xs font-medium" style={{ color: accentColor }}>
                    {plan.subtitle}
                  </p>
                  {isV2 && plan.key === "team" && (
                    <div
                      className="mt-2 flex items-center justify-between rounded-lg px-3 py-2"
                      style={{ background: "rgba(255,255,255,0.05)" }}
                    >
                      <span className="text-xs text-gray-300">Seats</span>
                      <span className="flex items-center gap-2.5">
                        <span
                          onClick={(e) => {
                            e.stopPropagation();
                            if (seats > TEAM_MIN_SEATS) setSeats((s) => Math.max(TEAM_MIN_SEATS, s - 1));
                          }}
                          className={`flex h-7 w-7 items-center justify-center rounded-lg text-base font-bold transition-opacity ${
                            seats <= TEAM_MIN_SEATS ? "cursor-not-allowed opacity-30" : "cursor-pointer hover:opacity-80"
                          }`}
                          style={{ background: `${accentColor}22`, color: accentColor }}
                        >
                          −
                        </span>
                        <span className="w-5 text-center text-base font-bold tabular-nums text-white">{seats}</span>
                        <span
                          onClick={(e) => {
                            e.stopPropagation();
                            if (seats < TEAM_MAX_SEATS) setSeats((s) => Math.min(TEAM_MAX_SEATS, s + 1));
                          }}
                          className={`flex h-7 w-7 items-center justify-center rounded-lg text-base font-bold transition-opacity ${
                            seats >= TEAM_MAX_SEATS ? "cursor-not-allowed opacity-30" : "cursor-pointer hover:opacity-80"
                          }`}
                          style={{ background: `${accentColor}22`, color: accentColor }}
                        >
                          +
                        </span>
                      </span>
                    </div>
                  )}
                  {/* ── Divider ── */}
                  <div className="my-3 h-px w-full" style={{ background: "rgba(255,255,255,0.06)" }} />

                  {/* ── Description (enterprise) or Features ── */}
                  {"description" in plan && plan.description ? (
                    <>
                      <p
                        className={`text-[11px] leading-relaxed text-gray-500 ${
                          plan.features?.length ? "" : "flex-1"
                        }`}
                      >
                        {plan.description}
                      </p>
                      {plan.features?.length > 0 && (
                        <ul className="mb-3 mt-2 flex flex-1 flex-col gap-2">
                          {plan.features.map((f: string) => (
                            <li key={f} className="flex items-start gap-2">
                              <CheckCircleIcon
                                className="mt-px h-3 w-3 flex-shrink-0"
                                style={{ color: accentColor }}
                                weight="fill"
                              />
                              <span className="text-[11px] leading-snug text-gray-400">{f}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                      <div
                        className="mt-auto rounded-lg py-2 text-center text-[11px] font-semibold"
                        style={{
                          background: isSelected ? "rgba(212,160,60,0.15)" : "rgba(255,255,255,0.04)",
                          border: `1px solid ${isSelected ? "rgba(212,160,60,0.4)" : "rgba(255,255,255,0.08)"}`,
                          color: isSelected ? "#d4a03c" : "#9ca3af",
                        }}
                      >
                        Book a Meeting →
                      </div>
                    </>
                  ) : (
                    <ul className="flex flex-col gap-2">
                      {plan.features.map((f: string) => (
                        <li key={f} className="flex items-start gap-2">
                          <CheckCircleIcon
                            className="mt-px h-3 w-3 flex-shrink-0"
                            style={{ color: accentColor }}
                            weight="fill"
                          />
                          <span className="text-[11px] leading-snug text-gray-400">{f}</span>
                        </li>
                      ))}
                      {plan.credits && (
                        <li className="flex flex-col gap-1.5">
                          <div className="flex items-start gap-2">
                            <CheckCircleIcon
                              className="mt-px h-3 w-3 flex-shrink-0"
                              style={{ color: accentColor }}
                              weight="fill"
                            />
                            <span className="text-[11px] leading-snug text-gray-400">
                              {isV2 && plan.key === "team"
                                ? `${(TEAM_CREDITS_PER_SEAT * seats).toLocaleString("en-IN")} AI-Credits (750 / user)`
                                : isV2
                                  ? `${plan.credits.total} AI-Credits`
                                  : `${plan.credits.total} Free AI-Credits`}
                            </span>
                          </div>
                          {plan.credits.rates.length > 0 && (
                            <div className="ml-5 flex flex-col gap-1.5">
                              {plan.credits.rates.map((r: { label: string; value: string }) => (
                                <div key={r.label} className="flex items-center justify-between rounded-md px-2 py-1" style={{ background: "rgba(255,255,255,0.05)" }}>
                                  <span className="text-[10px] text-gray-400">{r.label}</span>
                                  <span className="rounded-full px-1.5 py-0.5 text-[10px] font-semibold" style={{ background: `${accentColor}22`, color: accentColor }}>{r.value}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </li>
                      )}
                    </ul>
                  )}
                </button>
              );
            })}
          </div>

          {/* Footer */}
          <div className={isV2 ? "px-5 py-6" : "p-6"}>
            {error && (
              <p className="mb-4 rounded-lg bg-red-950/40 px-4 py-2 text-sm text-red-400 border border-red-900/50">{error}</p>
            )}

            {selectedPlan === "enterprise" ? (
              <button
                onClick={handleBookCall}
                className="block w-full rounded-xl py-3.5 text-center text-sm font-semibold text-white transition-opacity"
                style={{
                  background: "linear-gradient(135deg, #b45309 0%, #d97706 100%)",
                  boxShadow: "0 4px 20px rgba(180,130,60,0.3)",
                }}
              >
                Book a Meeting
              </button>
            ) : (
              <button
                onClick={handleSubscribe}
                disabled={loading}
                className="w-full rounded-xl py-3.5 text-sm font-semibold text-white transition-opacity disabled:opacity-60"
                style={{
                  background: selectedPlan === "pro"
                    ? "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)"
                    : "linear-gradient(135deg, #0ea5e9 0%, #6366f1 100%)",
                  boxShadow: selectedPlan === "pro"
                    ? "0 4px 20px rgba(99,102,241,0.35)"
                    : "0 4px 20px rgba(14,165,233,0.3)",
                }}
              >
                {loading
                  ? "Processing…"
                  : `Subscribe to ${selectedPlanData.label}${isV2 && selectedPlan === "team" ? ` (${seats} seats)` : ""} — ${subscribePrice}/mo + GST`}
              </button>
            )}

            <div className="mt-3 flex items-center justify-center gap-1.5 text-gray-600">
              <LockSimpleIcon className="h-3 w-3" weight="fill" />
              <span className="text-xs">
                {selectedPlan === "enterprise"
                  ? "Our team will reach out to schedule a meeting"
                  : "Secured by Razorpay · Cancel anytime"}
              </span>
            </div>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
};

export default SourcingPlanModal;
